import { useMemo } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores'
import { matchPath, useLocation } from 'react-router-dom'
import { getAppsByGroup } from 'shared/configs/appConfigs'
import { useCommonStore } from 'shared/stores/commonStore'
import { useTopDockStore } from 'shared/stores/topDockStore'
import { AppConfig } from 'shared/types/app'

import { APP_GROUPS } from './useTopDockApps'

export const useInitializeGroupAppOrder = () => {
  const pinnedAppIds = useTopDockStore(state => state.pinnedAppIds)
  const windows = useWindowStore(state => state.windows)
  const { apps: availableApps, isLoading } = useAvailableApps()
  const location = useLocation()
  const isDetached = useCommonStore(state => state.isDetached)
  const currentPath = location.pathname

  const currentRouteAppIds = useMemo(() => {
    if (!isDetached) return []

    const matchedApp = Object.values(availableApps).find(app =>
      matchPath(
        { path: app.path, end: app.routeProps?.exact ?? false },
        currentPath
      )
    )

    return matchedApp ? [matchedApp.id] : []
  }, [isDetached, currentPath, availableApps])

  const appsByGroup = useMemo(
    () =>
      pinnedAppIds.length || windows.length || currentRouteAppIds.length
        ? APP_GROUPS.map(group => {
            const windowIds = [
              ...windows.map(window => window.id as AppId),
              ...currentRouteAppIds
            ]
            const groupApps = getAppsByGroup(
              Object.values(availableApps),
              group,
              undefined,
              pinnedAppIds,
              windowIds
            )
            const appsInGroup = Object.values(groupApps)?.[0] || []
            const orderedApps = pinnedAppIds
              .map(appId => appsInGroup.find(app => app.id === appId))
              .filter((app): app is AppConfig => app !== undefined)
            const remainingApps = appsInGroup.filter(
              app => !pinnedAppIds.includes(app.id)
            )
            return { [group]: [...orderedApps, ...remainingApps] }
          })
        : [],
    [pinnedAppIds, windows, availableApps, currentRouteAppIds]
  )

  return { appsByGroup, isLoading }
}
